import {DateUnit} from "./dashboardService";
import {DateRange} from "../../components/datePicker/dateRangePicker";
import moment from "moment";
import {TimeFormat} from "../../util/enum";

export interface MenuData {
    count: number,
    totalCost?: number,
}

export interface MenuSource {
    activeCampaign?: MenuData,
    engagedPatient?: MenuData,
    newAppointment?: MenuData,
    savedTime?: MenuData,
    sentSMS?: MenuData,
}

export interface AppointmentReminderData {
    day: string,
    week: string,
    month: string,
    year: string,
    patientConfirmCount: number,
    patientOpenCount: number,
    appointmentReminderCount: number,
    noShowCount: number,
}

export interface AppointmentReminderSource {
    dateType: DateUnit,
    statistic: Array<AppointmentReminderData>
}

export interface Point {
    x: string | number,
    y: number,
}

export enum ChartsDataSortKey {
    appointmentReminder = 0,
    patientOpen,
    patientConfirm,
    noShow,
}

export interface ChartsData {
    sortKey: ChartsDataSortKey,
    name: string,
    lineColor: string,
    xAxisData: Array<string>,
    yAxisData: Array<number>,
}

export const prepareEChartsOptions = (chartsData: Array<ChartsData>) => {
    if (!chartsData.length) {
        return
    }

    const xAxisData = chartsData[0].xAxisData
    const rangeLength = xAxisData.length

    let axisInterval = 0
    if (rangeLength > 30 && rangeLength <= 180) {
        axisInterval = 6
    } else if (rangeLength > 180) {
        axisInterval = 29
    }

    const series = chartsData.map((ds) => {
        const {name, lineColor, yAxisData} = ds
        return {
            name: name,
            type: "line",
            smooth: true,
            lineStyle: {
                color: lineColor,
            },
            itemStyle: {
                color: lineColor,
            },
            emphasis: {
                focus: "series",
            },
            data: yAxisData,
        }
    })
    return {
        grid: { top: 8, right: 8, bottom: 36, left: 36 },
        tooltip: {
            trigger: "axis",
        },
        xAxis: {
            axisLabel: {
                interval: axisInterval,
                formatter: ((value: string) => moment(value).format("MM-DD")),
                rotate: 30,
            },
            data: xAxisData,
        },
        yAxis: {
            type: "value",
            // minInterval: 1,
        },
        dataZoom: [
            {
                type: "inside",
                disabled: rangeLength <= 90,
                start: 0,
                end: 100,
            },
            {
                type: "slider",
                show: rangeLength > 90,
                start: 0,
                end: 100,
            },
        ],
        series: series,
    }
}

const getRandomNum = (base: number) => {
    return Math.floor(Math.random() * base) + base
}

export const generateChartsDataFromSource = (source: AppointmentReminderSource, dateRange: DateRange): Array<ChartsData> => {
    const {statistic} = source
    const timeArr = generateTimelinesFrom(dateRange)

    let reminderDt: Array<number> = [],
        openDt: Array<number> = [],
        confirmDt: Array<number> = [],
        noShowsDt: Array<number> = []
    let timelines: Array<string> = []

    timeArr.forEach((time) => {
        const dt = statistic.find(({year, month, day}) => parseInt(year) === time.year && parseInt(month) === time.month && parseInt(day) === time.day)
        reminderDt.push((dt?.appointmentReminderCount || 0) + getRandomNum(120))
        openDt.push((dt?.patientOpenCount || 0) + getRandomNum(60))
        confirmDt.push((dt?.patientConfirmCount || 0) + getRandomNum(30))
        noShowsDt.push((dt?.noShowCount || 0) + getRandomNum(15))
        timelines.push(time.value)
    })

    const reminderChartsDt: ChartsData = {
        sortKey: ChartsDataSortKey.appointmentReminder,
        name: "Appointment Reminders (Primary)",
        lineColor: "#BDBDBD",
        xAxisData: timelines,
        yAxisData: reminderDt,
    }
    const openChartsDt: ChartsData = {
        sortKey: ChartsDataSortKey.patientOpen,
        name: "Patient Opens",
        lineColor: "#219653",
        xAxisData: timelines,
        yAxisData: openDt,
    }
    const confirmChartsDt: ChartsData = {
        sortKey: ChartsDataSortKey.patientConfirm,
        name: "Patient Confirms",
        lineColor: "#2F80ED",
        xAxisData: timelines,
        yAxisData: confirmDt,
    }
    const noShowsChartsDt: ChartsData = {
        sortKey: ChartsDataSortKey.noShow,
        name: "No-shows",
        lineColor: "#EB5757",
        xAxisData: timelines,
        yAxisData: noShowsDt,
    }
    return [reminderChartsDt, openChartsDt, confirmChartsDt, noShowsChartsDt]
}

export interface TimelineItem {
    year: number,
    month: number,
    day: number,
    value: string,
}

export const generateTimelinesFrom = (dateRange: DateRange): Array<TimelineItem> => {
    const {from, to} = dateRange
    const diff = moment(to).diff(moment(from), "day")
    let timeArr: Array<TimelineItem> = []
    for (let i = 0; i <= diff; i++) {
        const m = moment(from).add(i, "day")
        timeArr.push({
            year: m.year(),
            month: m.month() + 1,
            day: m.date(),
            value: m.format(TimeFormat.YYYYMMDD),
        })
    }
    return timeArr
}
